import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Box, Typography, IconButton, Skeleton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getReviewerDisplayName, getReviewerInitials } from './ReviewerCard';

/**
 * ReviewerSuggestions — horizontally scrollable strip of random reviewers.
 *
 * Shows exactly 3 avatars at a time; the rest (up to 20, provided by
 * GET /api/reviewers/suggested) are reached by swiping or the chevron buttons.
 */

const GAP_PX = 8;
const CELL_WIDTH = `calc((100% - ${GAP_PX * 2}px) / 3)`;

const ReviewerSuggestions = ({
  reviewers = [],
  loading = false,
  onSelect,
  language = 'TH',
  selectedId = null,
  sx = {},
}) => {
  const scrollRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const dragRef = useRef({ active: false, startX: 0, startScroll: 0, moved: false });

  // Mouse wheel → horizontal scroll (vertical wheel deltas included)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    const onWheel = (e) => {
      if (el.scrollWidth <= el.clientWidth) return;
      const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (!delta) return;
      e.preventDefault();
      el.scrollLeft += delta;
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // Click-and-drag panning (desktop). Touch swiping is native.
  const handlePointerDown = useCallback((e) => {
    if (e.pointerType === 'touch') return;
    const el = scrollRef.current;
    if (!el) return;
    dragRef.current = { active: true, startX: e.clientX, startScroll: el.scrollLeft, moved: false };
  }, []);

  const handlePointerMove = useCallback((e) => {
    const el = scrollRef.current;
    if (!el || !dragRef.current.active) return;
    const dx = e.clientX - dragRef.current.startX;
    if (Math.abs(dx) > 3) dragRef.current.moved = true;
    el.scrollLeft = dragRef.current.startScroll - dx;
  }, []);

  const endDrag = useCallback(() => {
    dragRef.current.active = false;
  }, []);

  // Suppress the click that ends a drag, so panning doesn't select a reviewer
  const guardedSelect = useCallback((reviewer) => {
    if (dragRef.current.moved) {
      dragRef.current.moved = false;
      return;
    }
    if (onSelect) onSelect(reviewer);
  }, [onSelect]);

  const title = language === 'TH'
    ? 'ผู้รีวิวแนะนำ'
    : language === 'CN'
      ? '推荐评论者'
      : 'Suggested reviewers';

  const scrollByPage = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth, behavior: 'smooth' });
  };

  const isEmpty = !loading && (!reviewers || reviewers.length === 0);

  return (
    <Box
      sx={{ position: 'relative', ...sx }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Typography sx={{
        fontSize: '0.68rem',
        fontWeight: 700,
        color: 'text.secondary',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        mb: 0.5,
      }}>
        {title}
      </Typography>

      {isEmpty && (
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', py: 1 }}>
          {language === 'TH' ? 'ยังไม่มีรายชื่อผู้รีวิวแนะนำ'
            : language === 'CN' ? '暂无推荐评论者'
              : 'No suggested reviewers available'}
        </Typography>
      )}

      <Box
        ref={scrollRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        onPointerCancel={endDrag}
        sx={{
          display: 'flex',
          gap: `${GAP_PX}px`,
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollSnapType: 'x proximity',
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-x',
          overscrollBehaviorX: 'contain',
          cursor: 'grab',
          '&:active': { cursor: 'grabbing' },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          pb: 0.5,
        }}
      >
        {loading
          ? [0, 1, 2].map((i) => (
            <Box key={`sk-${i}`} sx={{
              width: CELL_WIDTH,
              flex: `0 0 ${CELL_WIDTH}`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
            }}>
              <Skeleton variant="circular" width={44} height={44} />
              <Skeleton variant="text" width="70%" height={14} />
            </Box>
          ))
          : reviewers.map((reviewer) => {
            const name = getReviewerDisplayName(reviewer, language);
            const initials = getReviewerInitials(reviewer);
            const isSelected = selectedId != null && String(selectedId) === String(reviewer.id);

            return (
              <Box
                key={reviewer.id}
                onClick={() => guardedSelect(reviewer)}
                sx={{
                  width: CELL_WIDTH,
                  flex: `0 0 ${CELL_WIDTH}`,
                  scrollSnapAlign: 'start',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.4,
                  cursor: 'pointer',
                  py: 0.5,
                  borderRadius: '12px',
                  transition: 'background-color 0.15s ease',
                  backgroundColor: isSelected ? 'rgba(124, 58, 237, 0.08)' : 'transparent',
                  '&:hover': { backgroundColor: 'rgba(124, 58, 237, 0.06)' },
                }}
              >
                <Box sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  backgroundColor: '#7c3aed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: 15,
                  overflow: 'hidden',
                  border: isSelected ? '2px solid #6d28d9' : '2px solid transparent',
                  boxShadow: '0 3px 8px rgba(124, 58, 237, 0.25)',
                }}>
                  {reviewer.avatar_url ? (
                    <img
                      src={reviewer.avatar_url}
                      alt={name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : initials}
                </Box>

                <Typography sx={{
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: 'text.primary',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  textAlign: 'center',
                  px: 0.3,
                }}>
                  {name}
                </Typography>
              </Box>
            );
          })}
      </Box>

      {/* Desktop chevrons — only rendered when there is more than one page */}
      {!loading && reviewers.length > 3 && hovered && (
        <>
          <IconButton
            size="small"
            onClick={() => scrollByPage(-1)}
            aria-label="Previous reviewers"
            sx={{
              position: 'absolute',
              left: -6,
              top: '55%',
              transform: 'translateY(-50%)',
              backgroundColor: '#ffffff',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              width: 26,
              height: 26,
              '&:hover': { backgroundColor: '#f5f5f5' },
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 18 }} />
          </IconButton>

          <IconButton
            size="small"
            onClick={() => scrollByPage(1)}
            aria-label="More reviewers"
            sx={{
              position: 'absolute',
              right: -6,
              top: '55%',
              transform: 'translateY(-50%)',
              backgroundColor: '#ffffff',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              width: 26,
              height: 26,
              '&:hover': { backgroundColor: '#f5f5f5' },
            }}
          >
            <ChevronRightIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </>
      )}
    </Box>
  );
};

export default ReviewerSuggestions;
